import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { Observer, observer } from 'mobx-react';
import * as React from 'react';
import { DragDropContext, DropResult, Droppable, DroppableProvided } from 'react-beautiful-dnd';
import { toast } from 'sonner';
import { useDebounceValue } from '../../../../hooks/useDebounceValue';
import { CampaignType } from '../../../../models/AdminModels';
import { useAdminUserSession } from '../../../../models/hooks/adminStateHooks';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { useEventLogging } from '../../../../models/Logging';
import { getDefaultDateStringValue, getIndustryName } from '../../../../models/UiUtils';
import { invalidateBlogTemplateCategoryNames, useAdminTemplateSearchQuery } from '../../../../queries';
import * as AdminViewModels from '../../../../viewmodels/AdminViewModels';
import { KnownCategories } from '../../../../viewmodels/AppViewModels';
import {
	Dropdown,
	DropdownContent,
	DropdownEllipsisTrigger,
	DropdownItem,
} from '../../../../web/components-v2/Dropdown';
import { DeleteConfirmation } from '../../../../web/components/DeleteConfirmation';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { LocalNotificationObserver } from '../../../../web/components/LocalNotificationObserver';
import { Modal } from '../../../../web/components/Modal';
import { ISelectBoxOption, SelectBox } from '../../../../web/components/SelectBox';
import { SearchIcon } from '../../../../web/components/svgs/icons/SearchIcon';
import { TextInput } from '../../../../web/components/TextInput';
import { bs } from '../../../../web/styles/styles';
import {
	CampaignTemplatesByIndustrySearchTable,
	CampaignTemplatesByIndustrySearchTableCell,
	CampaignTemplatesByIndustrySearchTableRow,
} from '../CampaignTemplatesByIndustry/components/CampaignTemplatesByIndustryHwcSearchResults';
import { CopyTemplateLinkModal } from '../CopyTemplateLinkModal';
import { TemplateCategory } from '../TemplateCategory';
import { TemplateCategoryEditDropdownItem } from '../TemplateCategory/components/TemplateCategoryEditMoreMenuItem';
import { TemplateCategoryViewDropdownItem } from '../TemplateCategory/components/TemplateCategoryVeiwMoreMenuItem';
import { CampaignLibraryNewCategoryModal } from './components/CampaignLibraryNewCategoryModal';
import { styleSheet } from './styles';

interface IProps {
	categories: AdminViewModels.TemplateCategoriesViewModel;
	campaignType?: CampaignType;
	industry: Api.Industry;
	onClickNewButton: () => void;
}

function _CategoriesByIndustry({ categories, campaignType, industry, onClickNewButton }: IProps) {
	const userSession = useAdminUserSession();
	const errorMessages = useErrorMessages();
	const { logApiError, logEvent } = useEventLogging('CategoriesByIndustry');
	const [sortingCategory, setSortingCategory] = React.useState<AdminViewModels.TemplateCategoryViewModel>(null);
	const [selectedTemplateIds, setSelectedTemplateIds] = React.useState<string[]>([]);
	const [moveIndex, setMoveIndex] = React.useState<number>(1);
	const [isNewCategoryModalOpen, setIsNewCategoryModalOpen] = React.useState<boolean>(false);
	const [fromCategory, setFromCategory] = React.useState<AdminViewModels.TemplateCategoryViewModel>(undefined);
	const [isChangeCategoryModalOpen, setIsChangeCategoryModalOpen] = React.useState<boolean>(false);
	const [templateToChange, setTemplateToChange] =
		React.useState<AdminViewModels.TemplateWithStatusesViewModel>(undefined);
	const [copyTemplate, setCopyTemplate] = React.useState<AdminViewModels.TemplateWithStatusesViewModel>(undefined);
	const [isCopyLinkModalOpen, setIsCopyLinkModalOpen] = React.useState<boolean>(false);
	const [categoryToEdit, setCategoryToEdit] = React.useState<AdminViewModels.TemplateCategoryViewModel>(null);
	const [categoryToDelete, setCategoryToDelete] = React.useState<string>(null);
	const [isDeleting, setIsDeleting] = React.useState(false);
	const [selectedCategory, setSelectedCategory] = React.useState<ISelectBoxOption<string>>(undefined);
	const [isChangingCategory, setIsChangingCategory] = React.useState(false);
	const [searchInputValue, setSearchInputValue] = React.useState('');
	const debouncedSearchValue = useDebounceValue(searchInputValue);
	const isAdminTemplateSearchQueryEnabled = Boolean(debouncedSearchValue);
	const adminTemplateSearchQuery = useAdminTemplateSearchQuery({
		enabled: isAdminTemplateSearchQueryEnabled,
		request: {
			industry,
			section: (
				{
					[CampaignType.Email]: Api.TemplateSection.Email,
					[CampaignType.Social]: Api.TemplateSection.SocialMediaPost,
					[CampaignType.Blog]: Api.TemplateSection.Blog,
				} as Record<CampaignType, Api.TemplateSection>
			)[campaignType],
			searchValue: debouncedSearchValue,
		},
	});
	const onMoveIndexChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		setMoveIndex(parseInt(e.currentTarget.value, 10));
	};
	const moveTemplates = async (newIndex: number) => {
		const templates = sortingCategory.systemTemplates.filter(
			x => selectedTemplateIds.indexOf(x.templateReference.template.id) > -1
		);
		sortingCategory
			.reorder(templates, newIndex, campaignType)
			?.then(() => {
				logEvent('ReorderTemplate', {
					from: sortingCategory.systemTemplates.indexOf(templates[0]),
					id: selectedTemplateIds,
					industry: categories.industry,
					to: newIndex,
				});

				setSelectedTemplateIds([]);
				setSortingCategory(null);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				logApiError('Reorder-Error', err);
				toast.error(err.systemMessage ?? 'Unable to rearrange templates');
			});
	};
	const onClickMoveSelected = async () => {
		const newIndex = moveIndex - 1;
		await moveTemplates(newIndex);
	};
	const onClickMoveCancel = () => {
		setSelectedTemplateIds([]);
		setSortingCategory(null);
	};

	const onClickArchiveSelected = async () => {
		try {
			// Get the archived category
			const archived = categories.categories?.find(x => x.name === 'Archived');

			await sortingCategory
				.bulkArchive(selectedTemplateIds, industry)
				?.then(() => {
					logEvent('BulkArchiveTemplates', {
						id: selectedTemplateIds,
						industry: categories.industry,
					});

					setSelectedTemplateIds([]);
					setSortingCategory(null);

					// Reload categories to reflect changes
					categories.load();
					if (campaignType) {
						archived.load(campaignType);
					}
				})
				?.catch((err: Api.IOperationResultNoValue) => {
					logApiError('BulkArchive-Error', err);
					toast.error(err.systemMessage ?? 'Unable to archive templates');
				});
		} catch (error) {
			logApiError('BulkArchive-Error', error);
			errorMessages.pushApiError(error);
		}
	};

	const onClickArchiveAllSelected = async () => {
		try {
			// Get the archived category
			const archived = categories.categories?.find(x => x.name === 'Archived');

			await sortingCategory
				.bulkArchiveAll(selectedTemplateIds)
				?.then(() => {
					logEvent('BulkArchiveAllTemplates', {
						id: selectedTemplateIds,
						industry: categories.industry,
					});

					setSelectedTemplateIds([]);
					setSortingCategory(null);

					// Reload categories to reflect changes
					categories.load();
					if (campaignType) {
						archived.load(campaignType);
					}
				})
				?.catch((err: Api.IOperationResultNoValue) => {
					logApiError('BulkArchiveAll-Error', err);
					toast.error(err.systemMessage ?? 'Unable to archive templates');
				});
		} catch (error) {
			logApiError('BulkArchiveAll-Error', error);
			errorMessages.pushApiError(error);
		}
	};
	const onDragEnd = (result: DropResult) => {
		if (!result.destination) {
			return; // this can happen if dropping right on the edge of the droppable box I noticed
		}
		if (result.type === 'template') {
			// get the correct category
			const categoryOfTemplate = categories.categories.getById(result.destination.droppableId.split('%')[1]);
			const template = categoryOfTemplate.systemTemplates.find(
				x => x.templateReference.template.id === result.draggableId
			);
			categoryOfTemplate
				.reorder([template], result.destination.index, campaignType)
				?.then(() => {
					logEvent('ReorderTemplate', {
						from: categoryOfTemplate.systemTemplates.indexOf(template),
						id: template.refName,
						industry: categories.industry,
						to: result.destination.index,
					});
				})
				?.catch((err: Api.IOperationResultNoValue) => {
					logApiError('Reorder-Error', err);
					toast.error('Unable to rearrange templates');
				});
		} else {
			const toIndex = result.destination.index;
			const category = categories.categories.getById(result.draggableId);
			categories
				.reorderCategories(category, toIndex)
				?.then(() => {
					logEvent('ReorderCategory', {
						from: categories.categories.indexOf(category),
						id: category.name,
						industry: categories.industry,
						to: toIndex,
					});
				})
				?.catch((err: Api.IOperationResultNoValue) => {
					logApiError('Reorder-Error', err);
					toast.error('Unable to rearrange categories');
				});
		}
	};
	const onSetChangeCategoryModalOpen =
		(isOpen: boolean) =>
		(
			template?: AdminViewModels.TemplateWithStatusesViewModel,
			categoryFrom?: AdminViewModels.TemplateCategoryViewModel
		) => {
			setFromCategory(categoryFrom);
			setIsChangeCategoryModalOpen(isOpen);
			setTemplateToChange(template);
		};
	const setCopyLinkModalOpen = (open: boolean) => (template: AdminViewModels.TemplateWithStatusesViewModel) => {
		setCopyTemplate(template);
		setIsCopyLinkModalOpen(open);
	};
	const onDeleteCategory = (categoryName: string) => () => {
		setCategoryToDelete(categoryName);
	};
	const toggleSelectedTemplateIds = (valueToToggle: string, category: AdminViewModels.TemplateCategoryViewModel) => {
		const newSelectedTemplateIds = [...selectedTemplateIds];
		const currentIndex = selectedTemplateIds.indexOf(valueToToggle);
		if (currentIndex > -1) {
			newSelectedTemplateIds.splice(currentIndex, 1);
		} else {
			newSelectedTemplateIds.push(valueToToggle);
		}
		setSelectedTemplateIds(newSelectedTemplateIds);
		setSortingCategory(newSelectedTemplateIds.length > 0 ? category : null);
	};
	const onDeleteConfirmationFinished = async (shouldDelete: boolean) => {
		if (shouldDelete) {
			logEvent('DeleteCategory', { id: categoryToDelete });
			try {
				setIsDeleting(true);
				await categories.delete(categoryToDelete, campaignType);
				const uncategorized = categories.categories.find(x => x.name === 'Uncategorized');
				await uncategorized.load(campaignType);
				setIsDeleting(false);
			} catch (error) {
				logApiError('DeleteCategory-Error', error);
				errorMessages.pushApiError(error);
			}
		}
		setCategoryToDelete(null);
	};
	const onRequestNewCategoryClose = () => {
		setIsNewCategoryModalOpen(false);
	};

	const onSaveCategory = (name: string, type: Api.TemplateCategoryType, originalName?: string) => {
		const isEdit = !!originalName;
		const operation = isEdit ? categories.update(originalName, name, type) : categories.create(name, type);
		operation
			?.then(() => {
				if (campaignType === CampaignType.Blog) {
					invalidateBlogTemplateCategoryNames();
				}
				if (!isEdit) {
					categories.load();
				}
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				logApiError(isEdit ? 'UpdateCategory-Error' : 'CreateCategory-Error', err);
				errorMessages.pushApiError(err);
			});
		setIsNewCategoryModalOpen(false);
		setCategoryToEdit(null);
	};
	const onEditCategory = (category: AdminViewModels.TemplateCategoryViewModel) => () => {
		setCategoryToEdit(category);
	};
	const onRequestEditCategoryClose = () => {
		setCategoryToEdit(null);
	};
	const onRequestChangeCategoryClose = (_?: string, cancel?: boolean) => {
		if (cancel) {
			onSetChangeCategoryModalOpen(false)(null, null);
		}
	};
	const onDropdownChanged = (selectedOption: ISelectBoxOption<string>) => {
		setSelectedCategory(selectedOption);
	};
	const onChangeCategory =
		(result = selectedCategory) =>
		async () => {
			const toCategory =
				templateToChange.templateReference.template.templateType === Api.TemplateType.HtmlNewsletter
					? categories.categories?.find(x => x.name === 'HTML Newsletters')
					: categories.categories?.find(x => x.name === result.title);
			const hasAssociatedTemplates = templateToChange?.templateReference?.template?.associatedTemplates?.length > 0;
			const isSocialCombo =
				(campaignType === CampaignType.Social && hasAssociatedTemplates) ||
				(campaignType === CampaignType.Email && hasAssociatedTemplates);
			try {
				setIsChangingCategory(true);
				const fullTemplate = new AdminViewModels.TemplateEditViewModel(
					userSession,
					templateToChange.templateReference.template
				);
				let selectedCampaignType = campaignType;
				switch (fullTemplate.template.templateType) {
					case Api.TemplateType.SocialMediaPost:
						selectedCampaignType = CampaignType.Social;
						break;
					case Api.TemplateType.Email:
						selectedCampaignType = CampaignType.Email;
						break;
					case Api.TemplateType.Blog:
						selectedCampaignType = CampaignType.Blog;
						break;
					default:
						// Other template types (e.g. HtmlNewsletter) keep the campaignType passed in by the caller.
						break;
				}
				await fullTemplate.load(fullTemplate.template.id, selectedCampaignType);
				const currentStatus = fullTemplate.statuses.find(s => s.category === fromCategory.name)?.status;
				await toCategory.addStatuses(
					fullTemplate.template.id,
					[
						{
							category: toCategory.name,
							industry: toCategory.industry,
							status: currentStatus,
						},
					],
					selectedCampaignType,
					isSocialCombo
				);
				await fromCategory.removeStatuses(
					fullTemplate.template.id,
					[
						{
							category: fromCategory.name,
							industry: fromCategory.industry,
							status: currentStatus,
						},
					],
					selectedCampaignType,
					isSocialCombo
				);

				await categories.load();
				onSetChangeCategoryModalOpen(false)(null, null);
				setIsChangingCategory(false);
			} catch (error) {
				logApiError('ChangeCategory-Error', error);
				errorMessages.pushApiError(error);
				setIsChangingCategory(false);
			}
		};
	const cancelChangeCategory = () => {
		onSetChangeCategoryModalOpen(false)(null, null);
	};
	const onRequestCopyLinkClose = () => {
		setCopyLinkModalOpen(false)(null);
	};
	return (
		<div className='templates-by-industry'>
			<div className={css(bs.flex, bs.itemsCenter, bs.boxBorder, bs.justifyBetween, bs.pt2, bs.pb8)}>
				<div className={css(styleSheet.headerTitle)}>{getIndustryName(industry)}</div>
				<div className={css(bs.flex, bs.itemsCenter, bs.gap4)}>
					<TextInput
						inputId='admin-template-search-input'
						type='text'
						value={searchInputValue}
						onChange={ev => setSearchInputValue(ev.target.value)}
						leftAccessory={<SearchIcon className={css(bs.mr2)} />}
						className={css(bs.roundedLg)}
						placeholder='Search'
					/>
					{selectedTemplateIds?.length > 0 ? (
						<>
							<label>Insert at position</label>
							<TextInput
								className={css(styleSheet.throwTemplateInput)}
								inputId='throw-campaigns-input'
								onChange={onMoveIndexChanged}
								type='number'
								value={moveIndex}
							/>
							<button className={css(bs.ctaButton)} onClick={onClickMoveSelected}>
								<span>Move Selected</span>
							</button>

							<button className={css(bs.ctaButton)} onClick={onClickArchiveSelected}>
								<span>Archive</span>
							</button>

							<button className={css(bs.ctaButton)} onClick={onClickArchiveAllSelected}>
								<span>Archive All</span>
							</button>

							<button className={css(bs.ctaButton)} onClick={onClickMoveCancel}>
								<span>Cancel</span>
							</button>
						</>
					) : (
						<>
							<button className={css(bs.ctaButton)} onClick={onClickNewButton}>
								<span>New {campaignType === CampaignType.Blog ? 'Blog' : 'Campaign'}</span>
							</button>
							<button className={css(bs.ctaButton)} onClick={() => setIsNewCategoryModalOpen(true)}>
								<span>Add New Category</span>
							</button>
						</>
					)}
				</div>
			</div>
			<div className={css(styleSheet.body)}>
				{isAdminTemplateSearchQueryEnabled && adminTemplateSearchQuery.isPending ? (
					<div className={css(bs.flex, bs.wFull, bs.justifyCenter)}>
						<LoadingSpinner />
					</div>
				) : null}
				{adminTemplateSearchQuery.status === 'success' ? (
					<CampaignTemplatesByIndustrySearchTable
						firstColumnHeader={
							<>
								<strong>{adminTemplateSearchQuery.data.length}</strong> results for &quot;{debouncedSearchValue}&quot;
							</>
						}
					>
						{adminTemplateSearchQuery.data.map(template => {
							const templateVm = new AdminViewModels.TemplateWithStatusesViewModel(userSession, template, industry);
							return (
								<CampaignTemplatesByIndustrySearchTableRow key={`search-results-${template.template.id}`}>
									<CampaignTemplatesByIndustrySearchTableCell>
										<span className={css(bs.textBrandPrimary, bs.textBase)}>{template.template.name}</span>
									</CampaignTemplatesByIndustrySearchTableCell>
									<CampaignTemplatesByIndustrySearchTableCell>
										<span className={css(bs.textSm)}>
											{Object.keys(template.industries)
												.map(i => i)
												.join(', ')}
										</span>
									</CampaignTemplatesByIndustrySearchTableCell>
									<CampaignTemplatesByIndustrySearchTableCell>
										<time dateTime={`${template.template.creationDate}`} className={css(bs.textSm)}>
											{template.template.creationDate ? getDefaultDateStringValue(template.template.creationDate) : ''}
										</time>
									</CampaignTemplatesByIndustrySearchTableCell>
									<CampaignTemplatesByIndustrySearchTableCell>
										<Dropdown>
											<DropdownEllipsisTrigger />
											<DropdownContent>
												{campaignType !== CampaignType.Blog ? (
													<DropdownItem onClick={() => setCopyLinkModalOpen(true)(templateVm)}>Copy</DropdownItem>
												) : null}
												<TemplateCategoryEditDropdownItem
													campaignType={campaignType}
													template={templateVm}
													categories={categories}
													industry={industry}
												/>
												<TemplateCategoryViewDropdownItem
													campaignType={campaignType}
													categories={categories}
													template={templateVm}
													industry={industry}
												/>
											</DropdownContent>
										</Dropdown>
									</CampaignTemplatesByIndustrySearchTableCell>
								</CampaignTemplatesByIndustrySearchTableRow>
							);
						})}
					</CampaignTemplatesByIndustrySearchTable>
				) : null}
				{categories?.isBusy || !categories?.isLoaded ? (
					<LoadingSpinner />
				) : (
					<DragDropContext onDragEnd={onDragEnd}>
						<Droppable direction='vertical' droppableId={`${industry}`} type='category'>
							{(droppableProvided: DroppableProvided) => {
								return (
									<Observer>
										{() => {
											return (
												<div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
													{categories?.categories
														?.filter(x => {
															if (sortingCategory) {
																return x.name === sortingCategory.name;
															}
															const isSocial = campaignType === CampaignType.Social;
															return isSocial ? x.name !== 'HTML Newsletters' : x;
														})
														.map((c, idx) => {
															return (
																<TemplateCategory
																	categories={categories}
																	onChangeCategory={onSetChangeCategoryModalOpen(true)}
																	onCopyDirectLink={setCopyLinkModalOpen(true)}
																	onDeleteCategory={onDeleteCategory(c.name)}
																	onEditCategory={onEditCategory(c)}
																	campaignType={campaignType}
																	category={c}
																	key={c.name}
																	index={idx}
																	selectedTemplateIds={selectedTemplateIds}
																	toggleSelectedTemplateIds={toggleSelectedTemplateIds}
																/>
															);
														})}
												</div>
											);
										}}
									</Observer>
								);
							}}
						</Droppable>
					</DragDropContext>
				)}
			</div>
			<DeleteConfirmation
				bodyText={`Are you sure you'd like to delete ${categoryToDelete}? All templates in ${categoryToDelete} will be moved to the Uncategorized section.`}
				destructiveButtonText='Delete Category'
				isOpen={!!categoryToDelete}
				onFinish={onDeleteConfirmationFinished}
				showSpinner={isDeleting}
			/>
			<CampaignLibraryNewCategoryModal
				category={categoryToEdit}
				isOpen={isNewCategoryModalOpen || !!categoryToEdit}
				onClose={categoryToEdit ? onRequestEditCategoryClose : onRequestNewCategoryClose}
				onSaveClick={onSaveCategory}
			/>
			<Modal isOpen={isChangeCategoryModalOpen} onRequestClose={onRequestChangeCategoryClose} useDefaultHeader={true}>
				<div className={css(styleSheet.modalTitle)}>Change Category</div>
				<div className={css(styleSheet.inputTitle)}>Select a category to move this campaign to</div>
				<SelectBox
					menuClassName={`${css(styleSheet.dropdownMenu)}`}
					onSelectionChanged={onDropdownChanged}
					options={
						copyTemplate?.templateReference?.template?.templateType === Api.TemplateType.HtmlNewsletter
							? categories?.categories
									?.map(c => {
										if (c.name === 'HTML Newsletters') {
											return { title: c.name, value: c.name };
										}
									})
									.filter(x => !!x)
							: categories?.categories
									?.map(c => {
										if (
											!(
												c.name === KnownCategories.Featured ||
												c.name === 'Archived' ||
												c.name === 'Uncategorized' ||
												c.name === KnownCategories.HtmlNewsletters ||
												c.name === KnownCategories.FinraReviewed ||
												c.name === fromCategory?.name
											)
										) {
											return { title: c.name, value: c.name };
										}
									})
									.filter(x => !!x)
					}
					selectedOption={selectedCategory}
					placeholder={<span>Select a category...</span>}
					triggerClassName={`${css(styleSheet.dropdownTrigger)}`}
				/>
				<div className={css(styleSheet.modalButtons)}>
					<button className={css(bs.ctaButton)} onClick={onChangeCategory()} disabled={isChangingCategory}>
						<span>Change</span>
					</button>
					<button className={css(bs.ctaButtonReverse)} onClick={cancelChangeCategory}>
						<span>Cancel</span>
					</button>
				</div>
			</Modal>
			{copyTemplate?.templateReference?.template ? (
				<CopyTemplateLinkModal
					industry={industry}
					isOpen={isCopyLinkModalOpen}
					onRequestClose={onRequestCopyLinkClose}
					template={copyTemplate.templateReference.template}
				/>
			) : null}

			<LocalNotificationObserver
				topic={Topics.EDIT_SYSTEM_EMAIL_TEMPLATE}
				onNotificationReceived={() => {
					categories.load();
				}}
			/>
		</div>
	);
}

export const CategoriesByIndustry = observer(_CategoriesByIndustry);
